import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import DropDown from "../dropdown/DropDown";
import SecondaryButton from "../buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";

type DatePickerProps = {
  label?: string;
  seconds?: number | null;
  onChange?: (seconds: number) => void;
  mandatory?: boolean;
  disableBeforeToday?: boolean;
  disableAfterToday?: boolean;
  hide_year?: boolean;
  minDateSeconds?: number | null;
  maxDateSeconds?: number | null;
  transparent?: boolean;
};

const DatePicker = ({
  label = "Sélectionner une date",
  seconds = null,
  onChange = () => {},
  mandatory = false,
  disableBeforeToday = false,
  disableAfterToday = false,
  hide_year = false,
  minDateSeconds = null,
  maxDateSeconds = null,
  transparent = false,
}: DatePickerProps) => {
  const [displayDate, setDisplayDate] = useState(
    seconds ? new Date(seconds * 1000) : new Date(),
  );
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const datePickerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const daysOfWeek = ["Lu", "Ma", "Me", "Je", "Ve", "Sa", "Di"];
  const months = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  // Format date as DD/MM/YYYY
  const formatDate = (seconds: number): string => {
    const date = new Date(seconds * 1000);
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  };

  // Update input display when selected date changes
  useEffect(() => {
    setDisplayValue(seconds ? formatDate(seconds) : "");
  }, [seconds]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the date picker input
      if (datePickerRef.current && datePickerRef.current.contains(target)) {
        return;
      }

      // Check if click is inside the calendar dropdown
      if (calendarRef.current && calendarRef.current.contains(target)) {
        return;
      }

      // Check if click is inside any dropdown portal (year selector)
      // This checks for any element with role="listbox" or dropdown class
      const isInsidePortal =
        (target as Element).closest('[role="listbox"]') ||
        (target as Element).closest('ul[style*="position: fixed"]');

      if (isInsidePortal) {
        return;
      }

      // Click is outside - close the calendar
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Compute portal position when opening calendar
  useEffect(() => {
    if (isOpen && datePickerRef.current) {
      const updatePosition = () => {
        if (!datePickerRef.current) return;
        const rect = datePickerRef.current.getBoundingClientRect();
        const calendarHeight = 360;
        const spaceBelow = window.innerHeight - rect.bottom;
        const up = spaceBelow < calendarHeight;

        setDropUp(up);
        setPortalStyle({
          position: "fixed",
          left: rect.left,
          ...(up
            ? { bottom: window.innerHeight - rect.top + 4 }
            : { top: rect.bottom + 4 }),
          zIndex: 9999,
        });
      };

      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition, true);

      // set initial date to selected date or today
      if (seconds) {
        setDisplayDate(new Date(seconds * 1000));
      }

      return () => {
        window.removeEventListener("resize", updatePosition);
        window.removeEventListener("scroll", updatePosition, true);
      };
    }
  }, [isOpen]);

  // Generate calendar days for current month view
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Fix: Get day of week with Monday as 0, Sunday as 6
  const getFirstDayOfMonth = (year: number, month: number): number => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert Sunday (0) to 6, and shift others back by 1
  };

  // Check if a date is disabled
  const isDateDisabled = (date: Date): boolean => {
    // Reset time part for accurate comparison
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    // Check if date is before today (if disableBeforeToday is true)
    if (disableBeforeToday) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (compareDate < today) {
        return true;
      }
    }
    if (disableAfterToday) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (compareDate > today) {
        return true;
      }
    }
    // Check against minDateSeconds if provided
    if (minDateSeconds !== null) {
      const minDateSecondsTime = new Date(minDateSeconds * 1000);
      minDateSecondsTime.setHours(0, 0, 0, 0);
      if (compareDate < minDateSecondsTime) {
        return true;
      }
    }

    // Check against maxDateSeconds if provided
    if (maxDateSeconds !== null) {
      const maxDateSecondsTime = new Date(maxDateSeconds * 1000);
      maxDateSecondsTime.setHours(0, 0, 0, 0);
      if (compareDate > maxDateSecondsTime) {
        return true;
      }
    }

    return false; // Date is enabled
  };

  type CalendarDay = {
    day: number;
    currentMonth: boolean;
    date: Date;
    disabled: boolean;
  };

  const generateCalendarDays = (): CalendarDay[] => {
    const year = displayDate.getFullYear();
    const month = displayDate.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);

    const days: CalendarDay[] = [];

    // Previous month days
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDay - 1; i >= 0; i--) {
      const date = new Date(year, month - 1, prevMonthDays - i);
      days.push({
        day: prevMonthDays - i,
        currentMonth: false,
        date: date,
        disabled: isDateDisabled(date),
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({
        day: i,
        currentMonth: true,
        date: date,
        disabled: isDateDisabled(date),
      });
    }

    // Next month days
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      const date = new Date(year, month + 1, i);
      days.push({
        day: i,
        currentMonth: false,
        date: date,
        disabled: isDateDisabled(date),
      });
    }

    return days;
  };

  // Calendar navigation
  const prevMonth = () => {
    setDisplayDate(
      new Date(displayDate.getFullYear(), displayDate.getMonth() - 1, 1),
    );
  };

  const nextMonth = () => {
    setDisplayDate(
      new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 1),
    );
  };

  // Modified to check if date is disabled
  const handleSelectDay = (date: Date, disabled: boolean) => {
    if (disabled) return; // Don't select disabled dates

    const seconds = Math.floor(date.getTime() / 1000);
    onChange(seconds);
    setIsOpen(false);
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (date: Date): boolean => {
    if (!seconds) return false;
    const selected_date = new Date(seconds * 1000);
    return (
      date.getDate() === selected_date.getDate() &&
      date.getMonth() === selected_date.getMonth() &&
      date.getFullYear() === selected_date.getFullYear()
    );
  };

  const calendarDays = generateCalendarDays();
  const has_value = seconds !== null && seconds !== undefined && seconds !== 0;

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="text-h4 text-neutral-400 ">{label}</div>
      <div
        className={`${transparent ? "" : "bg-white shadow-sm"} rounded-xl relative w-full select-none`}
        ref={datePickerRef}
      >
        {/* Input Field */}
        <div
          tabIndex={0}
          onFocus={() => {
            setIsOpen(true);
          }}
          className={`px-3 w-full text-h2 rounded-xl cursor-pointer outline-none ${
            isOpen
              ? " ring-neutral-300 ring-4 "
              : mandatory && !has_value
                ? "border-2 border-red-300"
                : "outline-none border"
          } relative flex flex-row py-3 h-fit ${has_value ? "items-end" : "items-center"}`}
        >
          <input
            type="text"
            className="w-full text-h2 bg-transparent rounded-xl focus:outline-none cursor-pointer border-0"
            placeholder={label}
            value={hide_year ? displayValue.substring(0, 5) : displayValue}
            onChange={(e) => setDisplayValue(e.target.value)}
            readOnly
          />
          <div className="absolute right-3 top-1/3 text-gray-400 cursor-pointer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        </div>
        {/* Calendar Dropdown — rendered via portal to escape overflow:hidden parents */}
        {isOpen && createPortal(
          <div
            ref={calendarRef}
            style={portalStyle}
            className="w-fit bg-white rounded-xl shadow-lg border border-gray-200"
          >
            {/* Header with month and navigation */}
            <div className="flex items-center justify-between p-2 border-b">
              <button
                onClick={prevMonth}
                className="p-1 rounded hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div className="flex flex-row items-center  text-h2  gap-4 font-medium">
                <div className="py-2 ">{months[displayDate.getMonth()]}</div>
                <div className="w-100">
                  <DropDown
                    value={displayDate.getFullYear()}
                    onSelect={(year) =>
                      setDisplayDate(
                        new Date(year as number, displayDate.getMonth(), 1),
                      )
                    }
                    variant="small"
                    options={Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - 4 + i;
                      return { label: year.toString(), value: year };
                    })}
                  ></DropDown>
                </div>
              </div>
              <button
                onClick={nextMonth}
                className="p-1 rounded hover:bg-gray-100"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>

            {/* Days of week header */}
            <div className="grid grid-cols-7 gap-1 p-2">
              {daysOfWeek.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 p-2">
              {calendarDays.map((day, index) => (
                <div
                  key={index}
                  onClick={() =>
                    day.currentMonth && handleSelectDay(day.date, day.disabled)
                  }
                  className={`
                  h-8 w-10 text-center text-h2 flex items-center justify-center rounded-lg
                  ${!day.currentMonth ? "text-gray-300" : day.disabled ? "text-gray-300 cursor-not-allowed" : "hover:bg-neutral-50 cursor-pointer"}
                  ${isToday(day.date) && day.currentMonth && !day.disabled ? "border border-neutral-400" : ""}
                  ${isSelected(day.date) && !day.disabled ? "bg-neutral-900 text-white hover:bg-neutral-600" : ""}
                  ${day.disabled && day.currentMonth ? "bg-gray-50" : ""}
                `}
                >
                  {day.day}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="border-t p-2 flex justify-between items-center">
              <SecondaryButton
                variant="small"
                label="Aujourd'hui"
                onClick={() => {
                  const today = new Date();
                  const todaySeconds = Math.floor(today.getTime() / 1000);
                  // Only set to today if today is not disabled
                  if (!isDateDisabled(today)) {
                    onChange(todaySeconds);
                    setIsOpen(false);
                  }
                }}
              ></SecondaryButton>{" "}
              <button
                onClick={() => {
                  setDisplayValue("");
                  onChange(0);
                  setIsOpen(false);
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                <Icon1 name="effacer2" size={20} color={colors.neutral_400} />
              </button>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
};

export default DatePicker;
